import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '+31624359764';

const Contact = () => {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    onderwerp: '',
    bericht: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Save to database
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);

      if (response.status === 200) {
        // Create WhatsApp message
        const message = `🔔 *Nieuw Contact Bericht*\n\n` +
          `👤 *Naam:* ${formData.naam}\n` +
          `📧 *Email:* ${formData.email}\n` +
          `📱 *Telefoon:* ${formData.telefoon || 'Niet opgegeven'}\n` +
          `📋 *Onderwerp:* ${formData.onderwerp}\n\n` +
          `💬 *Bericht:*\n${formData.bericht}`;

        const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
        
        // Redirect to WhatsApp
        window.location.href = whatsappUrl;
        
        setSubmitted(true);

        // Reset form after redirect
        setTimeout(() => {
          setFormData({
            naam: '',
            email: '',
            telefoon: '',
            onderwerp: '',
            bericht: ''
          });
          setSubmitted(false);
        }, 1000);
      }
    } catch (err) {
      setError('Er ging iets mis bij het versturen. Probeer het opnieuw of neem direct contact op via telefoon.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/5876967/pexels-photo-5876967.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Contact"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/85 via-blue-900/75 to-blue-800/70" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Neem Contact Op
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            We helpen je graag met al je vragen over je droomreis
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">
                  Laten We Praten
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  Heb je vragen over een reis, wil je een vrijblijvend advies, of ben je klaar om je droomreis te boeken? Neem gerust contact met ons op!
                </p>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Email</h3>
                      <a
                        href="mailto:lemmensnikita@gmail.com"
                        className="text-gray-600 hover:text-blue-900 transition-colors break-all">Nikitasopmaatreizen@outlook.com


                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Telefoon</h3>
                      <a
                        href="tel:0624359764"
                        className="text-gray-600 hover:text-blue-900 transition-colors">

                        06 243 597 64
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Bereikbaarheid</h3>
                      <p className="text-gray-600">
                        Soest<br />
                        
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-10 p-6 bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl">
                  <h3 className="font-bold text-blue-900 mb-3 text-lg">
                    Snelle Reactietijd
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We proberen alle aanvragen binnen 24 uur te beantwoorden. Voor dringende zaken kun je ons direct bellen.
                  </p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-200">
                <h2 className="text-2xl font-bold text-blue-900 mb-6">
                  Stuur Ons Een Bericht
                </h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error &&
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  }
                  
                  <div>
                    <label htmlFor="naam" className="block text-sm font-medium text-gray-700 mb-2">
                      Naam *
                    </label>
                    <input
                      type="text"
                      id="naam"
                      name="naam"
                      value={formData.naam}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Je volledige naam" />

                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="je@email.nl" />

                  </div>

                  <div>
                    <label htmlFor="telefoon" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefoon
                    </label>
                    <input
                      type="tel"
                      id="telefoon"
                      name="telefoon"
                      value={formData.telefoon}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="06 12345678" />

                  </div>

                  <div>
                    <label htmlFor="onderwerp" className="block text-sm font-medium text-gray-700 mb-2">
                      Onderwerp *
                    </label>
                    <select
                      id="onderwerp"
                      name="onderwerp"
                      value={formData.onderwerp}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">

                      <option value="">Selecteer een onderwerp</option>
                      <option value="algemene-vraag">Algemene Vraag</option>
                      <option value="reis-op-maat">Reis op Maat</option>
                      <option value="pakketreis">Pakketreis Informatie</option>
                      <option value="offerte">Offerte Aanvragen</option>
                      <option value="anders">Anders</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="bericht" className="block text-sm font-medium text-gray-700 mb-2">
                      Bericht *
                    </label>
                    <textarea
                      id="bericht"
                      name="bericht"
                      value={formData.bericht}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      placeholder="Vertel ons over je reisplannen of stel je vraag..." />

                  </div>

                  <button
                    type="submit"
                    disabled={submitted || loading}
                    className={`w-full py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                    submitted ?
                    'bg-green-500 text-white' :
                    loading ?
                    'bg-gray-400 text-white cursor-wait' :
                    'bg-gradient-to-r from-blue-900 to-blue-700 text-white hover:from-blue-800 hover:to-blue-600 transform hover:scale-105'}`
                    }>

                    {submitted ?
                    <>
                        <Check className="w-5 h-5" />
                        Verzonden!
                      </> :
                    loading ?
                    <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Bezig met versturen...
                      </> :

                    <>
                        <Send className="w-5 h-5" />
                        Verstuur Bericht
                      </>
                    }
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Liever Direct Aan De Slag?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Gebruik onze interactieve tool om meteen je eigen reis te gaan plannen
            </p>
            <a
              href="/rondreizen-tool"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-900 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">

              Start met Plannen
              <Send className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default Contact;